/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement } from '@lexical/utils';
import {
  $createParagraphNode,
  $insertNodes,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  LexicalEditor,
} from 'lexical';
import * as React from 'react';
import { useEffect, useState } from 'react';

import {
  $createFirmwareTableNode,
  FirmwareTableNode,
} from '../../nodes/FirmwareTableNode.tsx';
import { Input } from '../../../../../../components/uikit/inputs/Input.tsx';
import {
  Button,
  ButtonColor,
} from '../../../../../../components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from '../../../../../../application/localization/useScopedTranslation.ts';
import styles from '../commonStyles.module.scss';

export const INSERT_FIRMWARE_TABLE_COMMAND: LexicalCommand<string> =
  createCommand('INSERT_FIRMWARE_TABLE_COMMAND');

export function InsertFirmwareTableDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}): React.JSX.Element {
  const [tableId, setTableId] = useState('');

  const i18n = useScopedTranslation('Page.PageDesigner');

  const onClick = () => {
    activeEditor.dispatchCommand(INSERT_FIRMWARE_TABLE_COMMAND, tableId);
    onClose();
  };

  return (
    <>
      <Input
        topHelperText={i18n.t('firmware_generation')}
        onChange={(e) => setTableId(e.target.value)}
        value={tableId}
        containerClassName={styles.marginTop20}
        fullWidth={true}
      />
      <div className={styles.buttonContainer}>
        <Button
          onClick={onClose}
          title={i18n.t('cancel')}
          color={ButtonColor.Secondary}
        />
        <Button
          onClick={onClick}
          title={i18n.t('insert_table')}
          color={ButtonColor.Primary}
          disabled={!tableId}
        />
      </div>
    </>
  );
}

export default function FirmwareTablePlugin(): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!editor.hasNodes([FirmwareTableNode])) {
      throw new Error(
        'TablePlugin: FirmwareTableNode not registered on editor',
      );
    }

    return editor.registerCommand<string>(
      INSERT_FIRMWARE_TABLE_COMMAND,
      (payload) => {
        const tableNode = $createFirmwareTableNode(payload);
        $insertNodes([tableNode]);
        if ($isRootOrShadowRoot(tableNode.getParentOrThrow())) {
          $wrapNodeInElement(tableNode, $createParagraphNode).selectEnd();
        }

        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);
  return null;
}
